/**
 * Hebrew Shva Na/Nach Detector
 * Identifies whether a shva (שְׁוָא) is:
 * - Shva Na (נָע) - mobile/pronounced
 * - Shva Nach (נָח) - silent/resting
 */

// Hebrew Unicode constants
const SHVA = '\u05B0'; // ְ
const DAGESH = '\u05BC'; // ּ

// Vowels (nikud)
const KAMATZ = '\u05B8'; // ָ (can be gadol or katan)
const PATACH = '\u05B7'; // ַ
const TZERE = '\u05B5'; // ֵ
const SEGOL = '\u05B6'; // ֶ
const CHIRIK = '\u05B4'; // ִ
const CHOLAM = '\u05B9'; // ֹ
const CHOLAM_CHASER = '\u05BA'; // ֺ
const KUBUTZ = '\u05BB'; // ֻ
const KAMATZ_KATAN = '\u05C7'; // ׇ
const CHATAF_PATACH = '\u05B2'; // ֲ
const CHATAF_SEGOL = '\u05B1'; // ֱ
const CHATAF_KAMATZ = '\u05B3'; // ֳ

// Hebrew letters
const YOD = '\u05D9'; // י

// Letter ranges
const HEBREW_LETTER_REGEX = /[\u05D0-\u05EA]/;

// Vowels considered "long" for shva rules
const LONG_VOWELS = new Set([
  KAMATZ, // Kamatz gadol (assuming gadol by default)
  TZERE, // Tzere
  CHOLAM, // Cholam
  CHOLAM_CHASER,
]);

// Vowels considered "short" for shva rules
const SHORT_VOWELS = new Set([
  PATACH, // Patach
  SEGOL, // Segol
  CHIRIK, // Chirik (chaser)
  KUBUTZ, // Kubutz
  KAMATZ_KATAN, // Kamatz katan
]);

// Chataf vowels (always indicate shva na on following letter)
const CHATAF_VOWELS = new Set([CHATAF_PATACH, CHATAF_SEGOL, CHATAF_KAMATZ]);

// All vowels (excluding shva)
const ALL_VOWELS = new Set([
  ...Array.from(LONG_VOWELS),
  ...Array.from(SHORT_VOWELS),
  ...Array.from(CHATAF_VOWELS),
]);

export interface ShvaInfo {
  index: number; // Position in the original string
  isNa: boolean; // true = shva na, false = shva nach
  letter: string; // The letter the shva is under
  reason: string; // Why it's na/nach (for debugging)
}

export interface LetterInfo {
  letter: string;
  index: number;
  vowel: string | null;
  hasShva: boolean;
  hasDagesh: boolean;
  shvaType: 'na' | 'nach' | null;
  shvaReason: string | null;
}

/**
 * Parse a Hebrew word into its component letters with their nikud
 */
function parseHebrewWord(word: string): LetterInfo[] {
  const letters: LetterInfo[] = [];
  let currentLetter: LetterInfo | null = null;

  for (let i = 0; i < word.length; i++) {
    const char = word[i];

    if (HEBREW_LETTER_REGEX.test(char)) {
      // Save previous letter
      if (currentLetter) {
        letters.push(currentLetter);
      }
      // Start new letter
      currentLetter = {
        letter: char,
        index: i,
        vowel: null,
        hasShva: false,
        hasDagesh: false,
        shvaType: null,
        shvaReason: null,
      };
    } else if (currentLetter) {
      // It's nikud - attach to current letter
      if (char === SHVA) {
        currentLetter.hasShva = true;
      } else if (char === DAGESH) {
        currentLetter.hasDagesh = true;
      } else if (ALL_VOWELS.has(char) || CHATAF_VOWELS.has(char)) {
        currentLetter.vowel = char;
      }
    }
  }

  // Don't forget the last letter
  if (currentLetter) {
    letters.push(currentLetter);
  }

  return letters;
}

/**
 * Determine if a vowel is "long" for shva rules
 * Special case: chirik before yod = chirik male (long)
 */
function isLongVowel(vowel: string | null, nextLetter: LetterInfo | null): boolean {
  if (!vowel) return false;

  // Chirik before yod without its own vowel = chirik male (long)
  if (vowel === CHIRIK && nextLetter?.letter === YOD && !nextLetter.vowel && !nextLetter.hasShva) {
    return true;
  }

  return LONG_VOWELS.has(vowel);
}

/**
 * Check if letter has a "chataf" vowel (half-vowel)
 */
function hasChatafVowel(letter: LetterInfo): boolean {
  return letter.vowel !== null && CHATAF_VOWELS.has(letter.vowel);
}

/**
 * Main function: Analyze shva types in a Hebrew word
 */
export function analyzeShvaInWord(word: string): LetterInfo[] {
  const letters = parseHebrewWord(word);

  for (let i = 0; i < letters.length; i++) {
    const letter = letters[i];
    const prevLetter = i > 0 ? letters[i - 1] : null;

    if (!letter.hasShva) continue;

    // Rule 1: Shva at the beginning of a word = Shva Na
    if (i === 0) {
      letter.shvaType = 'na';
      letter.shvaReason = 'Beginning of word';
      continue;
    }

    // Rule 2: Shva at end of word = Shva Nach
    if (i === letters.length - 1) {
      letter.shvaType = 'nach';
      letter.shvaReason = 'End of word';
      continue;
    }

    // Rule 3: Second of two consecutive shvas = Shva Na
    if (prevLetter?.hasShva && prevLetter.shvaType === 'nach') {
      letter.shvaType = 'na';
      letter.shvaReason = 'Second of two consecutive shvas';
      continue;
    }

    // Rule 4: After a chataf vowel = Shva Na
    if (prevLetter && hasChatafVowel(prevLetter)) {
      letter.shvaType = 'na';
      letter.shvaReason = 'After chataf vowel';
      continue;
    }

    // Rule 5: Under a letter with dagesh (in middle of word) = likely Shva Na
    // Dagesh chazak doubles the letter, so shva under it is na
    // Exception: dagesh kal at beginning doesn't apply here since we already handled position 0
    if (letter.hasDagesh && i > 0) {
      // This is likely dagesh chazak (doubling) - shva na
      letter.shvaType = 'na';
      letter.shvaReason = 'Letter has dagesh (chazak)';
      continue;
    }

    // Rule 6: After a long vowel = Shva Na
    if (prevLetter && isLongVowel(prevLetter.vowel, letter)) {
      letter.shvaType = 'na';
      letter.shvaReason = 'After long vowel';
      continue;
    }

    // Rule 7: After a short vowel = Shva Nach
    if (prevLetter?.vowel && SHORT_VOWELS.has(prevLetter.vowel)) {
      letter.shvaType = 'nach';
      letter.shvaReason = 'After short vowel';
      continue;
    }

    // Default: If previous letter has no vowel (closed syllable end), likely nach
    if (prevLetter && !prevLetter.vowel && !prevLetter.hasShva) {
      letter.shvaType = 'nach';
      letter.shvaReason = 'After letter with no vowel';
      continue;
    }

    // Fallback: Default to nach if unsure
    letter.shvaType = 'nach';
    letter.shvaReason = 'Default (uncertain)';
  }

  // Second pass: Handle consecutive shvas
  for (let i = 0; i < letters.length - 1; i++) {
    const current = letters[i];
    const next = letters[i + 1];

    // First of two consecutive shvas in middle of word = nach
    if (current.hasShva && next.hasShva && i > 0 && i < letters.length - 2) {
      if (current.shvaType !== 'nach') {
        current.shvaType = 'nach';
        current.shvaReason = 'First of two consecutive shvas';
      }
      if (next.shvaType !== 'na') {
        next.shvaType = 'na';
        next.shvaReason = 'Second of two consecutive shvas';
      }
    }
  }

  return letters;
}

/**
 * Get all shva positions and their types in a word
 */
export function getShvaPositions(word: string): ShvaInfo[] {
  const letters = analyzeShvaInWord(word);
  const shvas: ShvaInfo[] = [];

  for (const letter of letters) {
    if (letter.hasShva && letter.shvaType) {
      // Find the actual shva character position in the original string
      const letterStart = letter.index;
      let shvaIndex = -1;

      // Search for shva after this letter
      for (let j = letterStart + 1; j < word.length; j++) {
        if (HEBREW_LETTER_REGEX.test(word[j])) break; // Hit next letter
        if (word[j] === SHVA) {
          shvaIndex = j;
          break;
        }
      }

      if (shvaIndex !== -1) {
        shvas.push({
          index: shvaIndex,
          isNa: letter.shvaType === 'na',
          letter: letter.letter,
          reason: letter.shvaReason || '',
        });
      }
    }
  }

  return shvas;
}

/**
 * Render a Hebrew word with shva na highlighted
 * Returns an array of segments with styling info
 * Only the shva character itself (ְ) is marked, not the whole letter
 */
export interface WordSegment {
  text: string;
  isShvaNa: boolean;
  isShvaNach: boolean;
}

export function segmentWordByShva(word: string): WordSegment[] {
  const shvas = getShvaPositions(word);

  if (shvas.length === 0) {
    return [{ text: word, isShvaNa: false, isShvaNach: false }];
  }

  const segments: WordSegment[] = [];
  let lastIndex = 0;

  for (const shva of shvas) {
    // Add text before this shva
    if (shva.index > lastIndex) {
      segments.push({
        text: word.slice(lastIndex, shva.index),
        isShvaNa: false,
        isShvaNach: false,
      });
    }

    // Add the shva character with its type
    segments.push({
      text: word[shva.index],
      isShvaNa: shva.isNa,
      isShvaNach: !shva.isNa,
    });

    lastIndex = shva.index + 1;
  }

  // Add remaining text
  if (lastIndex < word.length) {
    segments.push({
      text: word.slice(lastIndex),
      isShvaNa: false,
      isShvaNach: false,
    });
  }

  return segments;
}

/**
 * Debug function: Print analysis of a word
 */
export function debugWord(word: string): void {
  console.log(`\nAnalyzing: ${word}`);
  const letters = analyzeShvaInWord(word);

  for (const letter of letters) {
    if (letter.hasShva) {
      console.log(
        `  ${letter.letter}: Shva ${letter.shvaType?.toUpperCase()} - ${letter.shvaReason}`,
      );
    }
  }
}
